// Layer 2 — "Show the check". The quote the finding rests on, and the working.
//
// Collapsed by default: a reviewer should be able to read a finding and decide
// on it without opening this. Open by default for the selected finding when
// there are few enough findings that opening it costs nothing.

import { Disclosure, Label, Quote } from '../primitives';
import { ModelAssistedCaveat } from '../ModelLayerNotice';
import Receipt from './Receipt';
import type { Flag } from '../../types';

/** Long quotes clamp; the control lengthens them rather than hiding them. */
export const QUOTE_CLAMP_LINES = 6;

/**
 * Whether there is a check to show at all. A finding with neither a quote nor
 * a receipt would open onto an empty panel, so the layer is not rendered.
 */
export function hasCheck(finding: Flag): boolean {
  return finding.quote !== null || finding.receipt !== undefined;
}

interface CheckReceiptProps {
  finding: Flag;
  defaultOpen?: boolean;
}

/**
 * Layer 2's contents, apart from the disclosure that holds them.
 *
 * Split out so that the printed review — which has no disclosures, because
 * paper does not expand — renders the same layer the screen does rather than a
 * second implementation of it that can drift from this one.
 */
export function CheckBody({ finding }: { finding: Flag }) {
  const arithmetic = finding.layer === 'deterministic';
  const cite = [
    finding.sectionRef?.trim() || null,
    finding.page !== null ? `page ${finding.page}` : null,
  ]
    .filter((p): p is string => p !== null)
    .join(' · ');

  return (
    <>
      {/* A model found the clause; a person has to confirm it. It belongs
          beside the quote, which is the thing to confirm it against. */}
      {arithmetic ? null : <ModelAssistedCaveat />}

      {finding.quote !== null ? (
        <Quote
          tone={arithmetic ? 'blue' : 'amber'}
          cite={cite || undefined}
          clampLines={QUOTE_CLAMP_LINES}
        >
          {finding.quote}
        </Quote>
      ) : (
        <div className="fc__absence">
          <Label>Nothing to quote</Label>
          <p className="fc__absence-body">
            This finding is an absence: the provision is not in the document.
            What is quoted elsewhere in this review is what the plan does say
            about the same subject.
          </p>
        </div>
      )}

      {finding.receipt ? <Receipt receipt={finding.receipt} /> : null}
    </>
  );
}

export default function CheckReceipt({
  finding,
  defaultOpen = false,
}: CheckReceiptProps) {
  if (!hasCheck(finding)) return null;

  return (
    <Disclosure
      className="fc__layer fc__layer--check"
      summary="Show the check"
      defaultOpen={defaultOpen}
      panelClassName="fc__layer-body"
    >
      <CheckBody finding={finding} />
    </Disclosure>
  );
}
